{ min, max } := Math

export clamp := (x: number, low: number, high: number) =>
  min high, max low, x

/**
 * Create a sha256 digest of the given buffer.
 */
digest := (buffer: ArrayBuffer) ->
  crypto.subtle.digest("SHA-256", buffer)

/**
 * Converts an ArrayBuffer to a base64 string.
 */
bufferToBase64 := (buffer: ArrayBuffer) ->
  btoa String.fromCharCode.apply null, new Uint8Array(buffer) as! number[]

/**
 * URL-safe base64 encoding.
 * Replaces '+' with '-', '/' with '_', and removes trailing '='.
 */
base64URLEncode := (base64String: string) ->
  base64String.replace(/\+/g, "-").replace(/\//g, "_").replace(/\=/g, "")

/**
 * Remove an item from an array in-place.
 */
export remove := <T>(array: T[], item: T) ->
  index := array.indexOf(item)
  if index >= 0
    array.splice(index, 1)

  return array

/**
 * Returns a URL-safe SHA-256 hash of the given blob.
 */
export urlSafeSHA256 := (blob: Blob) =>
  await blob.arrayBuffer()
  |> digest |> await
  |> bufferToBase64
  |> base64URLEncode

/**
 * Group an array of objects based on the result of a function
 * applied to each.
 */
export groupBy := <T>(array: T[], fn: (x: T) => PropertyKey) =>
  array.reduce (result, item) =>
    (result[fn(item)] ?= []).push item

    return result
  , {} as Record<PropertyKey, T[]>

/** Limit promise requests with the same key to only one in flight */
export promiseChoke := <T extends (...args: any[]) => Promise<any>>(fn: T): T =>
  cache := new Map<Parameters<T>[0], ReturnType<T>>

  ((...args: Parameters<T>) =>
    [key] := args

    if cache.has(key)
      return cache.get(key)!

    p := fn(...args).finally =>
      cache.delete key

    cache.set key, p as ReturnType<T>

    return p
  ) as T

/** Wrap a function with a deprecation message */
export deprecationWarning := <T extends (...args: any[]) => any>(msg: string | T, fn: T) =>
  if typeof msg is "function"
    fn = msg
    msg = "DEPRECATED"

  (this: ThisParameterType<T>, ...args: Parameters<T>) ->
    console.warn msg
    fn.apply(this, args)
